import React, { useState } from 'react';
import { X } from 'lucide-react';

interface TagsInputProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  required?: boolean;
  placeholder?: string;
  helpText?: string;
  maxLength?: number;
}

export default function TagsInput({
  label,
  value = [],
  onChange,
  error,
  required = false,
  placeholder = 'Type and press Enter',
  helpText,
  maxLength
}: TagsInputProps) {
  const [inputValue, setInputValue] = useState('');

  // Ensure value is always an array
  const tags = Array.isArray(value) ? value : [];

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault();

      if (maxLength && inputValue.length > maxLength) {
        return;
      }

      if (!tags.includes(inputValue.trim())) {
        onChange([...tags, inputValue.trim()]);
      }
      setInputValue('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    onChange(tags.filter(tag => tag !== tagToRemove));
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>

      <div className={`border rounded-md px-3 py-2 focus-within:ring-2  ${
        error
          ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500'
          : 'border-gray-300 focus-within:ring-blue-500 focus-within:border-blue-500'
      }`}>
        <div className="flex flex-wrap gap-2 mb-2">
          {tags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-md text-sm"
            >
              {tag}
              <button
                type="button"
                onClick={() => removeTag(tag)}
                className="hover:text-blue-600"
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>

        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={value.length === 0 ? placeholder : 'Add more...'}
          maxLength={maxLength}
          className="w-full outline-none text-sm"
        />
      </div>

      {maxLength && inputValue && !error && (
        <p className="mt-1 text-xs text-gray-500">
          {inputValue.length}/{maxLength} characters
        </p>
      )}
      {helpText && !error && (
        <p className="mt-1 text-sm text-gray-500">{helpText}</p>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
}